import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { MobileTradingAccountCards } from '@pages/Traders-Room/Trading-Accounts/Live/MobileTradingAccountCards'
import { DynamicLeverageModal } from '@pages/Traders-Room/Trading-Accounts/modals/DynamicLeverage.modal'
import { LeverageModal } from '@pages/Traders-Room/Trading-Accounts/modals/Leverage.modal'
import {
  ChangeLeverageModalType,
  TradingAccountsChangeLeverageModal,
} from '@pages/Traders-Room/Trading-Accounts/modals/TradingAccountsChangeLeverage.modal'

import { useModal } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSnackbar } from '@context/SnackbarContext'
import { useWalletsReadContext } from '@context/UserWalletContext'

import { Pagination } from '@ui/v2/Pagination/Pagination'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { AccountGroupType } from '@model/AccountGroupType'
import { NameDto } from '@model/NameDto'
import {
  isMT4PlatformType,
  isMT5PlatformType,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'
import { DemoTradingAccount, TradingAccount, TradingAccountStatusType } from '@model/TradingAccount'

import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'
import { InformationModal } from '@shared/modals/Information.modal'

import { LiveTradingAccountsCards } from './LiveTradingAccountsCards'

export const LiveTradingAccountsPage: React.FC = () => {
  const { t } = useTranslation()
  const { product } = useProductReadContext()
  const { addSnackbar } = useSnackbar()
  const isMobile = useWindowResize()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const tradingAccountParam = params.get('tradingAccountId')
  const { wallets = [] } = useWalletsReadContext()

  const dynamicLeverageModal = useModal(DynamicLeverageModal)
  const leverageModal = useModal(LeverageModal)
  const changeLeverageModal = useModal(TradingAccountsChangeLeverageModal)
  const changeLeverageSuccessModal = useModal(InformationModal, {
    title: t('Trading Account.Leverage changed'),
  })

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const tableQuery = usePaginatedQuery({
    queryKey: queryKeys.tradingAccount.live(),
    queryFn: (q) =>
      apiClient.getTradingAccounts({
        ...q,
        search: `(AccountGroupType == ${AccountGroupType.Live}) AND ((Status == ${TradingAccountStatusType.Approved}) OR (Status == ${TradingAccountStatusType.CloseOnly}))`,
      }),
  })
  const { forceRefresh } = tableQuery
  const { data: platformTypes = [] } = useQuery({
    queryKey: queryKeys.tradingAccount.platformTypes(product),
    queryFn: () => apiClient.getPlatformTypes(product),
  })
  const onlyCFDAccounts = tableQuery.data.filter(
    (acc) =>
      isMT4PlatformType(acc.platformType.id) ||
      isMT5PlatformType(acc.platformType.id) ||
      isTMTPlatformType(acc.platformType.id) ||
      isTradingViewPlatformType(acc.platformType.id)
  )

  const handleChangeLeverage = async (
    ta: TradingAccount | DemoTradingAccount,
    oldLeverage: NameDto,
    leverage: NameDto
  ) => {
    const modalData: ChangeLeverageModalType = {
      visible: true,
      previousValue: oldLeverage,
      newValue: leverage,
      step: 1,
      data: ta,
    }

    changeLeverageModal.open({
      data: modalData,
      onProceed: () => handleConfirmChangeLeverage(modalData),
    })
    leverageModal.close()
  }

  const handleConfirmChangeLeverage = async (data: ChangeLeverageModalType) => {
    try {
      if (data.data && data.previousValue && data.newValue) {
        await apiClient.updateTradingAccountLeverage(data.data.id, {
          leverageTypeId: data.newValue.id,
        })
        queryClient.invalidateQueries({ queryKey: ['tradingAccount'] })
        forceRefresh()
      }
      changeLeverageModal.close()
      changeLeverageSuccessModal.open({
        description: t('Trading Account.Leverage changed from to', {
          oldValue: data.previousValue?.name,
          newValue: data.newValue?.name,
        }),
      })
    } catch (error: unknown) {
      changeLeverageSuccessModal.close()
      console.error(error, 'error')
    }
  }

  const handleOpenDynamicLeverageInfoModal = () => {
    dynamicLeverageModal.open()
  }

  const handleLeverageChange = ({
    tradingAccount,
    restricted,
  }: {
    tradingAccount: TradingAccount
    restricted?: boolean
  }) => {
    if (restricted) {
      addSnackbar.error({ message: t('This action is not available') })
      return
    }

    if (
      isTMTPlatformType(tradingAccount?.platformType.id) ||
      isTMTPlatformType(tradingAccount?.parentPlatformType?.id)
    ) {
      return dynamicLeverageModal.open()
    }

    return leverageModal.open({
      platformTypeId: tradingAccount.platformType.id,
      currentLeverageId: tradingAccount.leverageType.id,
      restrictions: [],
      onSelect: (selectedLeverage) =>
        handleChangeLeverage(tradingAccount, tradingAccount.leverageType, selectedLeverage),
    })
  }

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    const newPageQuery = {
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    }
    tableQuery.setPageQuery(newPageQuery)
  }

  const Content = () =>
    isMobile ? (
      <MobileTradingAccountCards
        tableQuery={{
          ...tableQuery,
          data: onlyCFDAccounts,
        }}
        wallets={wallets}
        platformTypes={platformTypes}
        tradingAccountParam={tradingAccountParam}
        onLeverageChange={handleLeverageChange}
        onDisplayDynamicLeverageInfo={handleOpenDynamicLeverageInfoModal}
      />
    ) : (
      <LiveTradingAccountsCards
        tableQuery={{
          ...tableQuery,
          data: onlyCFDAccounts,
        }}
        wallets={wallets}
        platformTypes={platformTypes}
        pageQuery={tableQuery.pageQuery}
        setPageQuery={tableQuery.setPageQuery}
        onLeverageChange={handleLeverageChange}
        onDisplayDynamicLeverageInfo={handleOpenDynamicLeverageInfoModal}
      />
    )

  return (
    <Loading isLoading={tableQuery.isLoading}>
      <Content />
      {tableQuery.meta && tableQuery.hasInitialResults && (
        <Pagination
          scrollToHeaderId={ScrollToIds.IBClientsHeader}
          maxPageSize={100}
          pageData={tableQuery.meta}
          isLoading={tableQuery.isLoading}
          onPageChanged={handlePageChange}
        />
      )}
    </Loading>
  )
}
